import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Paper,
  Chip,
  Divider,
  useTheme,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  alpha,
  Switch,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
  Close,
  ContentCopy,
  Check,
  Code,
  Settings,
  PlayArrow,
  ExpandMore,
  TuneOutlined,
  BugReportOutlined,
  WebhookOutlined,
  BuildOutlined,
  InfoOutlined,
  WarningAmberOutlined,
  CheckCircleOutlined,
  ErrorOutlineOutlined,
  LightbulbOutlined,
  LockOutlined,
  PublicOutlined,
  TouchAppOutlined,
} from "@mui/icons-material";
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

interface SnifferCDNDocumentationProps {
  open?: boolean;
  onClose?: () => void;
  mode?: "dialog" | "page";
  projectDetails: any;
}

type WidgetMode = 'authenticated' | 'public' | 'trigger';

const highlightSyntax = (code: string): string => {
  let result = code
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Placeholders use \x00_N_\x00 format — the underscores prevent
  // \b(\d+)\b (step 7) from matching the index digits, since _ is
  // a word character so no word-boundary fires around the number.
  const placeholders: string[] = [];
  const ph = (html: string): string => {
    const i = placeholders.length;
    placeholders.push(html);
    return `\x00_${i}_\x00`;
  };

  // 1. Comments
  result = result.replace(/(\/\/[^\n]*)/g, (m) =>
    ph(`<span style="color:#6a9955">${m}</span>`)
  );

  // 2. Strings (single and double quoted)
  result = result.replace(/('(?:[^'\\\n]|\\.)*'|"(?:[^"\\\n]|\\.)*")/g, (m) =>
    ph(`<span style="color:#ce9178">${m}</span>`)
  );

  // 3. HTML tags (escaped as &lt;...&gt;)
  result = result.replace(
    /(&lt;\/?)(script|head|body)(.*?)(&gt;)/g,
    (_, open, tag, attrs, close) =>
      ph(
        `<span style="color:#808080">${open}</span><span style="color:#569cd6">${tag}</span><span style="color:#808080">${attrs}${close}</span>`
      )
  );

  // 4. Keywords
  result = result.replace(
    /\b(var|let|const|function|if|else|try|catch|return|new|typeof|throw)\b/g,
    (m) => ph(`<span style="color:#c586c0">${m}</span>`)
  );

  // 5. Built-in objects
  result = result.replace(
    /\b(document|console|window|SnifferWidget)\b/g,
    (m) => ph(`<span style="color:#4ec9b0">${m}</span>`)
  );

  // 6. Literals
  result = result.replace(
    /\b(true|false|null|undefined)\b/g,
    (m) => ph(`<span style="color:#569cd6">${m}</span>`)
  );

  // 7. Numbers
  result = result.replace(/\b(\d+)\b/g, (m) =>
    ph(`<span style="color:#b5cea8">${m}</span>`)
  );

  // 8. Property names in object literals (word before colon, not URL protocol)
  result = result.replace(/\b([a-zA-Z_$][\w$]*)(?=\s*:(?!\/\/))/g, (m) => {
    if (m.includes('\x00')) return m;
    return ph(`<span style="color:#9cdcfe">${m}</span>`);
  });

  // 9. Method calls (.name followed by paren)
  result = result.replace(/\.([a-zA-Z_$][\w$]*)(?=\s*\()/g, (full, name) => {
    if (full.includes('\x00')) return full;
    return '.' + ph(`<span style="color:#dcdcaa">${name}</span>`);
  });

  // Restore placeholders (single pass — placeholders never nest)
  return result.replace(/\x00_(\d+)_\x00/g, (_, idx) => placeholders[parseInt(idx)]);
};

const CodeBlock = ({
  code,
  language = "html",
}: {
  code: string;
  language?: string;
}) => {
  const [copied, setCopied] = useState(false);
  const theme = useTheme();
  const { t } = useTranslation(["bugherd"]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const highlightedCode = useMemo(() => highlightSyntax(code), [code]);

  return (
    <Paper
      elevation={0}
      sx={{
        position: "relative",
        backgroundColor: "#1e1e1e",
        borderRadius: 2,
        overflow: "hidden",
        border: `1px solid ${theme.palette.mode === "dark" ? theme.palette.divider : "#333"}`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          py: 1,
          borderBottom: "1px solid #333",
          backgroundColor: "#252526",
        }}
      >
        <Chip
          label={language.toUpperCase()}
          size="small"
          sx={{
            fontSize: "0.7rem",
            height: 20,
            backgroundColor: "#3c3c3c",
            color: "#d4d4d4",
          }}
        />
        <Tooltip title={copied ? t("Copied!") : t("Copy code")}>
          <IconButton size="small" onClick={handleCopy} sx={{ color: "#d4d4d4" }}>
            {copied ? (
              <Check fontSize="small" sx={{ color: "#4ec9b0" }} />
            ) : (
              <ContentCopy fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Box>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 2,
          overflow: "auto",
          maxHeight: 400,
          fontSize: "0.85rem",
          fontFamily: "'Fira Code', 'Monaco', 'Consolas', monospace",
          lineHeight: 1.6,
          color: "#d4d4d4",
        }}
      >
        <code dangerouslySetInnerHTML={{ __html: highlightedCode }} />
      </Box>
    </Paper>
  );
};

const ConfigCard = ({
  name,
  type,
  required,
  description,
  defaultValue,
  toggleEnabled,
  onToggle,
  isToggled,
}: {
  name: string;
  type: string;
  required?: boolean;
  description: string;
  defaultValue?: string;
  toggleEnabled?: boolean;
  onToggle?: () => void;
  isToggled?: boolean;
}) => {
  const theme = useTheme();
  const { t } = useTranslation(["bugherd"]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: `1px solid ${isToggled ? theme.palette.primary.main : theme.palette.divider}`,
        backgroundColor: isToggled
          ? alpha(theme.palette.primary.main, 0.05)
          : alpha(theme.palette.background.paper, 0.6),
        transition: "all 0.2s ease",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        "&:hover": {
          borderColor: theme.palette.primary.main,
          backgroundColor: alpha(theme.palette.primary.main, 0.02),
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: 1, flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", minWidth: 0, flex: 1 }}>
          <Typography
            component="code"
            sx={{
              fontFamily: "'Fira Code', 'Monaco', monospace",
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              fontSize: "0.75rem",
              fontWeight: 500,
              wordBreak: "break-word",
            }}
          >
            {name}
          </Typography>
          {required && (
            <Chip
              label={t("Required")}
              size="small"
              color="error"
              sx={{ height: 20, fontSize: "0.65rem", fontWeight: 600 }}
            />
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {toggleEnabled && (
            <Switch
              size="small"
              checked={isToggled}
              onChange={onToggle}
              color="primary"
            />
          )}
          <Chip
            label={type}
            size="small"
            sx={{
              height: 22,
              fontSize: "0.7rem",
              fontWeight: 500,
              flexShrink: 0,
              backgroundColor: alpha(
                type === "string"
                  ? theme.palette.success.main
                  : type === "boolean"
                  ? theme.palette.info.main
                  : theme.palette.warning.main,
                0.1
              ),
              color:
                type === "string"
                  ? theme.palette.success.main
                  : type === "boolean"
                  ? theme.palette.info.main
                  : theme.palette.warning.main,
            }}
          />
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: defaultValue ? 1.5 : 0, lineHeight: 1.5, flex: 1 }}>
        {description}
      </Typography>
      {defaultValue && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            pt: 1.5,
            borderTop: `1px dashed ${theme.palette.divider}`,
            mt: "auto",
          }}
        >
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            {t("Default:")}
          </Typography>
          <Typography
            component="code"
            sx={{
              fontFamily: "'Fira Code', 'Monaco', monospace",
              fontSize: "0.75rem",
              color: theme.palette.text.primary,
              backgroundColor: theme.palette.mode === "dark" ? "#2d2d2d" : "#f5f5f5",
              px: 1,
              py: 0.25,
              borderRadius: 0.5,
            }}
          >
            {defaultValue}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

const CallbackCard = ({
  name,
  params,
  description,
  toggleEnabled,
  onToggle,
  isToggled,
  modeTag,
}: {
  name: string;
  params: string;
  description: string;
  toggleEnabled?: boolean;
  onToggle?: () => void;
  isToggled?: boolean;
  modeTag?: string;
}) => {
  const theme = useTheme();
  const purpleColor = "#8b5cf6";

  const getModeTagColor = (tag: string) => {
    switch (tag) {
      case 'Trigger': return '#f59e0b';
      case 'Auth': return '#3b82f6';
      default: return '#10b981';
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: `1px solid ${isToggled ? purpleColor : theme.palette.divider}`,
        backgroundColor: isToggled
          ? alpha(purpleColor, 0.05)
          : alpha(theme.palette.background.paper, 0.6),
        transition: "all 0.2s ease",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        "&:hover": {
          borderColor: purpleColor,
          backgroundColor: alpha(purpleColor, 0.02),
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1, flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography
            component="code"
            sx={{
              fontFamily: "'Fira Code', 'Monaco', monospace",
              backgroundColor: alpha(purpleColor, 0.1),
              color: purpleColor,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              fontSize: "0.8rem",
              fontWeight: 500,
            }}
          >
            {name}
          </Typography>
          <Typography
            component="code"
            sx={{
              fontFamily: "'Fira Code', 'Monaco', monospace",
              fontSize: "0.75rem",
              color: theme.palette.text.secondary,
            }}
          >
            ({params})
          </Typography>
          {modeTag && (
            <Chip
              label={modeTag}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                fontWeight: 600,
                backgroundColor: alpha(getModeTagColor(modeTag), 0.1),
                color: getModeTagColor(modeTag),
              }}
            />
          )}
        </Box>
        {toggleEnabled && (
          <Switch
            size="small"
            checked={isToggled}
            onChange={onToggle}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: purpleColor,
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: purpleColor,
              },
            }}
          />
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5, flex: 1 }}>
        {description}
      </Typography>
    </Paper>
  );
};

const MethodCard = ({
  name,
  returns,
  description,
  modeTag,
}: {
  name: string;
  returns: string;
  description: string;
  modeTag?: string;
}) => {
  const theme = useTheme();
  const { t } = useTranslation(["bugherd"]);

  const getModeTagColor = (tag: string) => {
    switch (tag) {
      case 'Trigger': return '#f59e0b';
      case 'Auth': return '#3b82f6';
      default: return '#10b981';
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: alpha(theme.palette.background.paper, 0.6),
        transition: "all 0.2s ease",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        "&:hover": {
          borderColor: theme.palette.info.main,
          backgroundColor: alpha(theme.palette.info.main, 0.02),
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1, flexWrap: "wrap", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography
            component="code"
            sx={{
              fontFamily: "'Fira Code', 'Monaco', monospace",
              backgroundColor: alpha(theme.palette.info.main, 0.1),
              color: theme.palette.info.main,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              fontSize: "0.8rem",
              fontWeight: 500,
            }}
          >
            {name}
          </Typography>
          {modeTag && (
            <Chip
              label={modeTag}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                fontWeight: 600,
                backgroundColor: alpha(getModeTagColor(modeTag), 0.1),
                color: getModeTagColor(modeTag),
              }}
            />
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {t("returns")}
          </Typography>
          <Chip
            label={returns}
            size="small"
            sx={{
              height: 20,
              fontSize: "0.7rem",
              backgroundColor: alpha(theme.palette.info.main, 0.1),
              color: theme.palette.info.main,
            }}
          />
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5, flex: 1 }}>
        {description}
      </Typography>
    </Paper>
  );
};

type AlertType = "info" | "warning" | "success" | "error" | "tip";

const StyledAlert = ({
  type,
  title,
  children,
}: {
  type: AlertType;
  title?: string;
  children: React.ReactNode;
}) => {
  const theme = useTheme();

  const alertConfig = {
    info: {
      icon: <InfoOutlined />,
      color: "#3b82f6",
      bgColor: "#eff6ff",
      darkBgColor: "#1e3a5f",
      borderColor: "#93c5fd",
      darkBorderColor: "#1d4ed8",
    },
    warning: {
      icon: <WarningAmberOutlined />,
      color: "#f59e0b",
      bgColor: "#fffbeb",
      darkBgColor: "#422006",
      borderColor: "#fcd34d",
      darkBorderColor: "#b45309",
    },
    success: {
      icon: <CheckCircleOutlined />,
      color: "#10b981",
      bgColor: "#ecfdf5",
      darkBgColor: "#064e3b",
      borderColor: "#6ee7b7",
      darkBorderColor: "#047857",
    },
    error: {
      icon: <ErrorOutlineOutlined />,
      color: "#ef4444",
      bgColor: "#fef2f2",
      darkBgColor: "#450a0a",
      borderColor: "#fca5a5",
      darkBorderColor: "#b91c1c",
    },
    tip: {
      icon: <LightbulbOutlined />,
      color: "#8b5cf6",
      bgColor: "#f5f3ff",
      darkBgColor: "#2e1065",
      borderColor: "#c4b5fd",
      darkBorderColor: "#6d28d9",
    },
  };

  const config = alertConfig[type];
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        p: 2,
        borderRadius: 2,
        backgroundColor: isDark ? config.darkBgColor : config.bgColor,
        border: `1px solid ${isDark ? config.darkBorderColor : config.borderColor}`,
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          backgroundColor: config.color,
          borderRadius: "4px 0 0 4px",
        },
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          backgroundColor: alpha(config.color, 0.15),
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          "& svg": {
            fontSize: 18,
            color: config.color,
          },
        }}
      >
        {config.icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {title && (
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: isDark ? alpha(config.color, 0.9) : config.color,
              mb: 0.5,
            }}
          >
            {title}
          </Typography>
        )}
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            lineHeight: 1.6,
            "& code": {
              backgroundColor: alpha(config.color, 0.1),
              color: config.color,
              px: 0.75,
              py: 0.25,
              borderRadius: 0.5,
              fontSize: "0.8rem",
              fontFamily: "'Fira Code', 'Monaco', monospace",
            },
          }}
        >
          {children}
        </Typography>
      </Box>
    </Box>
  );
};

// Configuration toggle interface
interface ConfigToggles {
  errorCaptureEnabled: boolean;
  autoCreateBugs: boolean;
  maxErrors: boolean;
  captureConsole: boolean;
  captureNetwork: boolean;
  captureGlobalErrors: boolean;
  onBugCreated: boolean;
  onError: boolean;
  triggerQueryParam: boolean;
  triggerQueryParamCustom: boolean;
}

const DECRYPTION_KEY = "2c571b3d1b697c9054a0cec5885319ef5572600975e3a9cbbdc7ab353154311a";

const SnifferCDNDocumentation = ({
  open = false,
  onClose,
  mode = "dialog",
  projectDetails,
}: SnifferCDNDocumentationProps) => {
  const { t } = useTranslation(["bugherd"]);
  const theme = useTheme();
  const { id: projectId } = useParams<{ id: string }>();

  // State for widget mode selection
  const [selectedMode, setSelectedMode] = useState<WidgetMode>('authenticated');
  const [customTriggerParam, setCustomTriggerParam] = useState('report-bug');


  // State for configuration toggles
  const [configToggles, setConfigToggles] = useState<ConfigToggles>({
    errorCaptureEnabled: true,
    autoCreateBugs: false,
    maxErrors: false,
    captureConsole: false,
    captureNetwork: false,
    captureGlobalErrors: false,
    onBugCreated: false,
    onError: false,
    triggerQueryParam: false,
    triggerQueryParamCustom: false,
  });

  const handleToggleChange = (key: keyof ConfigToggles) => {
    setConfigToggles(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleModeChange = (_: React.MouseEvent<HTMLElement>, newMode: WidgetMode | null) => {
    if (newMode === null) return;
    setSelectedMode(newMode);
    // Reset trigger-specific toggles when switching away from trigger
    if (newMode !== 'trigger') {
      setConfigToggles(prev => ({
        ...prev,
        triggerQueryParam: false,
        triggerQueryParamCustom: false,
      }));
    }
  };

  const snifferCdnUrl = import.meta.env.VITE_SNIFFER_CDN_URL || "https://dev.integration.sniffer.dev/sniffer-widget.js";
  const scriptTagCode = `<script src="${snifferCdnUrl}"></script>`;

  // Generate dynamic code based on selected mode and enabled toggles
  const dynamicCode = useMemo(() => {
    const pid = projectId || 'YOUR_PROJECT_ID';
    const cid = projectDetails.companyId || 'YOUR_COMPANY_ID';

    // Build errorCapture options
    const errorCaptureOptions: string[] = [
      `          enabled: true`,
      `          projectId: '${pid}'`,
    ];
    if (configToggles.autoCreateBugs) {
      errorCaptureOptions.push(`          autoCreateBugs: true`);
    }
    if (configToggles.maxErrors) {
      errorCaptureOptions.push(`          maxErrors: 200`);
    }
    if (configToggles.captureConsole) {
      errorCaptureOptions.push(`          captureConsole: true`);
    }
    if (configToggles.captureNetwork) {
      errorCaptureOptions.push(`          captureNetwork: true`);
    }
    if (configToggles.captureGlobalErrors) {
      errorCaptureOptions.push(`          captureGlobalErrors: true`);
    }

    // Build callback options
    const callbackOptions: string[] = [];
    if (configToggles.onBugCreated) {
      callbackOptions.push(`        onBugCreated: function(bugLink) {
          console.log('Bug created:', bugLink);
        }`);
    }
    if (configToggles.onError) {
      callbackOptions.push(`        onError: function(error) {
          console.error('Widget error:', error);
        }`);
    }

    const useVar = selectedMode === 'trigger';
    const varPrefix = useVar ? 'var widget = ' : '';

    // Build init options
    const initOptions: string[] = [
      `        decryptionKey: '${DECRYPTION_KEY}'`,
    ];

    if (selectedMode !== 'authenticated') {
      initOptions.push(`        mode: '${selectedMode}'`);
      initOptions.push(`        projectId: '${pid}'`);
      initOptions.push(`        companyId: '${cid}'`);
    }

    // Trigger-specific: triggerQueryParam
    if (selectedMode === 'trigger' && configToggles.triggerQueryParam) {
      if (configToggles.triggerQueryParamCustom && customTriggerParam) {
        initOptions.push(`        triggerQueryParam: '${customTriggerParam}'`);
      } else {
        initOptions.push(`        triggerQueryParam: true`);
      }
    }

    let code = `<script>
  document.addEventListener('DOMContentLoaded', function() {
    try {
      ${varPrefix}SnifferWidget.init({
${initOptions.join(',\n')},`;

    if (configToggles.errorCaptureEnabled) {
      code += `
        errorCapture: {
${errorCaptureOptions.join(',\n')},
        },`;
    }

    if (callbackOptions.length > 0) {
      code += `\n${callbackOptions.join(',\n')},`;
    }

    code += `
      });`;

    // Trigger mode: add show() example and event listener
    if (selectedMode === 'trigger') {
      code += `

      // Show the widget when needed
      document.getElementById('report-bug-btn').addEventListener('click', function() {
        widget.show();
      });

      // Listen for completion
      widget.on('trigger:operationComplete', function(result) {
        if (result.success) {
          console.log('Bug created:', result.bugLink, result.bugId);
        } else if (result.cancelled) {
          console.log('User cancelled');
        }
      });`;
    }

    code += `
    } catch (error) {
      console.error('Failed to initialize widget:', error);
    }
  });
</script>`;

    return code;
  }, [configToggles, projectId, projectDetails.companyId, selectedMode, customTriggerParam]);

  // Mode descriptions for the contextual alert
  const modeDescriptions: Record<WidgetMode, string> = {
    authenticated: t("Full-featured mode. Users must log in before accessing capture features. All API calls use the authenticated session token. The floating button appears immediately."),
    public: t("All capture features available without login. Uses public API endpoints that require a projectId and companyId instead of a session token. The floating button appears immediately."),
    trigger: t("Programmatic control mode. The floating button is hidden by default and only appears when you call widget.show(). Uses public (unauthenticated) endpoints. The button auto-hides when the user completes or cancels an operation."),
  };

  // Content component to be shared between dialog and page modes
  const DocumentationContent = (
    <Box sx={{ maxWidth: 1000, mx: "auto" }}>
          {/* Introduction */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom fontWeight={500}>
              {t("Overview")}
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              {t(
                "Live Logs Docs is an automatic error tracking tool that seamlessly integrates into your web application. It captures JavaScript errors, console errors, network failures, and unhandled promise rejections, automatically creating bug reports to help you identify and resolve issues faster."
              )}
            </Typography>
            <Box sx={{ mt: 2 }}>
              <StyledAlert type="info" title={t("Before you begin")}>
                {selectedMode === 'authenticated'
                  ? t("Make sure you have your project's decryption key ready before starting the integration.")
                  : t("Make sure you have your project's decryption key, project ID, and company ID ready before starting the integration.")
                }
              </StyledAlert>
            </Box>
          </Box>

          {/* Mode Selector */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom fontWeight={500}>
              {t("Widget Mode")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("Select how you want the widget to operate. The code snippet below will update automatically.")}
            </Typography>
            <ToggleButtonGroup
              value={selectedMode}
              exclusive
              onChange={handleModeChange}
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                '& .MuiToggleButtonGroup-grouped': {
                  border: 'none',
                  '&:not(:first-of-type)': {
                    borderRadius: '12px',
                    ml: 0,
                  },
                  '&:first-of-type': {
                    borderRadius: '12px',
                  },
                },
              }}
            >
              <ToggleButton
                value="authenticated"
                sx={{
                  flex: 1,
                  minWidth: 180,
                  p: 2,
                  textTransform: 'none',
                  border: `1px solid ${theme.palette.divider} !important`,
                  borderRadius: '12px !important',
                  '&.Mui-selected': {
                    border: `2px solid ${theme.palette.primary.main} !important`,
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    },
                  },
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                  <LockOutlined sx={{ fontSize: 28, color: selectedMode === 'authenticated' ? theme.palette.primary.main : theme.palette.text.secondary }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {t("Authenticated")}
                    </Typography>
                    <Chip label={t("Default")} size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600 }} color="primary" />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                    {t("Login required, full features")}
                  </Typography>
                </Box>
              </ToggleButton>
              <ToggleButton
                value="public"
                sx={{
                  flex: 1,
                  minWidth: 180,
                  p: 2,
                  textTransform: 'none',
                  border: `1px solid ${theme.palette.divider} !important`,
                  borderRadius: '12px !important',
                  '&.Mui-selected': {
                    border: `2px solid ${theme.palette.primary.main} !important`,
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    },
                  },
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                  <PublicOutlined sx={{ fontSize: 28, color: selectedMode === 'public' ? theme.palette.primary.main : theme.palette.text.secondary }} />
                  <Typography variant="subtitle2" fontWeight={600}>
                    {t("Public")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                    {t("No login, public endpoints")}
                  </Typography>
                </Box>
              </ToggleButton>
              <ToggleButton
                value="trigger"
                sx={{
                  flex: 1,
                  minWidth: 180,
                  p: 2,
                  textTransform: 'none',
                  border: `1px solid ${theme.palette.divider} !important`,
                  borderRadius: '12px !important',
                  '&.Mui-selected': {
                    border: `2px solid ${theme.palette.primary.main} !important`,
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    },
                  },
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                  <TouchAppOutlined sx={{ fontSize: 28, color: selectedMode === 'trigger' ? theme.palette.primary.main : theme.palette.text.secondary }} />
                  <Typography variant="subtitle2" fontWeight={600}>
                    {t("Trigger")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                    {t("Programmatic control")}
                  </Typography>
                </Box>
              </ToggleButton>
            </ToggleButtonGroup>

            <Box sx={{ mt: 2 }}>
              <StyledAlert type="tip" title={t(selectedMode.charAt(0).toUpperCase() + selectedMode.slice(1) + " Mode")}>
                {modeDescriptions[selectedMode]}
              </StyledAlert>
            </Box>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Installation Steps */}
          <Typography variant="h6" gutterBottom fontWeight={500}>
            {t("Installation Steps")}
          </Typography>

          {/* Step 1: Add Script Tag */}
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              mt: 3,
              borderRadius: 2,
              borderLeft: `4px solid ${theme.palette.primary.main}`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  backgroundColor: theme.palette.primary.main,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                }}
              >
                1
              </Box>
              <Code color="primary" />
              <Typography variant="subtitle1" fontWeight={600}>
                {t("Add Script Tag")}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t(
                "Add the following script tag inside the <head> section of your HTML document. This loads the Sniffer Widget library."
              )}
            </Typography>
            <CodeBlock code={scriptTagCode} language="html" />
            <Box sx={{ mt: 2 }}>
              <StyledAlert type="warning" title={t("Important")}>
                {t(
                  "Place this script tag before any other scripts that might depend on the Sniffer Widget."
                )}
              </StyledAlert>
            </Box>
          </Paper>

          {/* Step 2: Initialize Widget */}
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              mt: 3,
              borderRadius: 2,
              borderLeft: `4px solid ${theme.palette.primary.main}`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  backgroundColor: theme.palette.primary.main,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                }}
              >
                2
              </Box>
              <Settings color="primary" />
              <Typography variant="subtitle1" fontWeight={600}>
                {t("Initialize Widget")}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t(
                "Add the initialization script inside the <body> section of your HTML document, preferably at the end before the closing </body> tag. Enable additional options below to customize the snippet."
              )}
            </Typography>
            <CodeBlock code={dynamicCode} language="javascript" />
            <Box sx={{ mt: 2 }}>
              <StyledAlert type="tip" title={t("Customize Your Snippet")}>
                {t(
                  "Toggle the switches in the Configuration Reference section below to add more options to your snippet. The code above will update automatically."
                )}
              </StyledAlert>
            </Box>
          </Paper>

          {/* Step 3: Verify Installation */}
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              mt: 3,
              borderRadius: 2,
              borderLeft: `4px solid ${theme.palette.primary.main}`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  backgroundColor: theme.palette.primary.main,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                }}
              >
                3
              </Box>
              <PlayArrow color="primary" />
              <Typography variant="subtitle1" fontWeight={600}>
                {t("Verify Installation")}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("To verify your installation:")}
            </Typography>
            <Box component="ol" sx={{ pl: 2, mb: 0 }}>
              <Typography component="li" variant="body2" color="text.secondary">
                {t("Open your browser's Developer Tools (F12)")}
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                {t("Check the Console tab for any initialization errors")}
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                {t("Look for 'Live Logs Docs initialized' message in the console")}
              </Typography>
              {selectedMode === 'trigger' ? (
                <Typography component="li" variant="body2" color="text.secondary">
                  {t("Call widget.show() or use a trigger query parameter to verify the widget appears")}
                </Typography>
              ) : (
                <Typography component="li" variant="body2" color="text.secondary">
                  {t("Trigger a test error (e.g., throw new Error('test')) to verify error capture")}
                </Typography>
              )}
            </Box>
            <Box sx={{ mt: 2 }}>
              <StyledAlert type="success" title={t("Success Indicator")}>
                {selectedMode === 'trigger'
                  ? t("If you see 'Live Logs Docs initialized' in the console, your integration is successful! The widget will appear when you call widget.show() or use a trigger query parameter.")
                  : t("If you see 'Live Logs Docs initialized' in the console, your integration is successful! Errors will now be automatically captured and bugs will be created.")
                }
              </StyledAlert>
            </Box>
          </Paper>

          <Divider sx={{ my: 4 }} />

          {/* Configuration Reference */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" fontWeight={500}>
              {t("Configuration Reference")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t("Explore all available configuration options to customize the widget behavior.")}
            </Typography>
          </Box>

          {/* Core Options Accordion */}
          <Accordion
            defaultExpanded
            elevation={0}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "12px !important",
              mb: 2,
              "&:before": { display: "none" },
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <TuneOutlined sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {t("Core Options")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("Essential configuration settings")}
                  </Typography>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <ConfigCard
                    name="decryptionKey"
                    type="string"
                    required
                    description={t(
                      "Your unique project decryption key for secure communication."
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <ConfigCard
                    name="mode"
                    type="string"
                    required={selectedMode !== 'authenticated'}
                    description={t(
                      "Widget operating mode. Options: 'authenticated' (default), 'public', or 'trigger'."
                    )}
                    defaultValue="'authenticated'"
                  />
                </Grid>
                {selectedMode !== 'authenticated' && (
                  <>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <ConfigCard
                        name="projectId"
                        type="string"
                        required
                        description={t(
                          "Your project ID. Required for public and trigger modes to associate bugs with the correct project."
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <ConfigCard
                        name="companyId"
                        type="string"
                        required
                        description={t(
                          "Your company ID. Required for public and trigger modes for backend validation."
                        )}
                      />
                    </Grid>
                  </>
                )}
                {selectedMode === 'trigger' && (
                  <Grid size={{ xs: 12 }}>
                    <ConfigCard
                      name="triggerQueryParam"
                      type="boolean | string"
                      description={t(
                        "Auto-show the widget when a URL query parameter is present. Set to true to watch for ?sniffer, or provide a custom parameter name."
                      )}
                      defaultValue="false"
                      toggleEnabled
                      isToggled={configToggles.triggerQueryParam}
                      onToggle={() => handleToggleChange('triggerQueryParam')}
                    />
                    {configToggles.triggerQueryParam && (
                      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Switch
                          size="small"
                          checked={configToggles.triggerQueryParamCustom}
                          onChange={() => handleToggleChange('triggerQueryParamCustom')}
                          color="primary"
                        />
                        <Typography variant="body2" color="text.secondary">
                          {t("Use custom parameter name")}
                        </Typography>
                        {configToggles.triggerQueryParamCustom && (
                          <TextField
                            size="small"
                            value={customTriggerParam}
                            onChange={(e) => setCustomTriggerParam(e.target.value)}
                            placeholder="report-bug"
                            sx={{
                              width: 200,
                              '& .MuiInputBase-input': {
                                fontFamily: "'Fira Code', 'Monaco', monospace",
                                fontSize: '0.85rem',
                              },
                            }}
                          />
                        )}
                      </Box>
                    )}
                  </Grid>
                )}
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Error Capture Accordion */}
          <Accordion
            defaultExpanded
            elevation={0}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "12px !important",
              mb: 2,
              "&:before": { display: "none" },
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{
                backgroundColor: alpha(theme.palette.error.main, 0.04),
                "&:hover": {
                  backgroundColor: alpha(theme.palette.error.main, 0.08),
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.error.main, 0.1),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <BugReportOutlined sx={{ color: theme.palette.error.main, fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {t("Error Capture Configuration")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("Automatic error tracking settings")}
                  </Typography>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 2 }}>
              <Box sx={{ mb: 2, p: 1.5, backgroundColor: alpha(theme.palette.info.main, 0.08), borderRadius: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {t("Toggle switches to add options to your snippet. Required options are always included.")}
                </Typography>
              </Box>
              {selectedMode !== 'authenticated' && (
                <Box sx={{ mb: 2 }}>
                  <StyledAlert type="info">
                    {t("The projectId in errorCapture is inherited from the core configuration above.")}
                  </StyledAlert>
                </Box>
              )}
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <ConfigCard
                    name="errorCapture.enabled"
                    type="boolean"
                    description={t(
                      "Enable automatic error capture and reporting."
                    )}
                    defaultValue="true"
                    toggleEnabled
                    isToggled={configToggles.errorCaptureEnabled}
                    onToggle={() => handleToggleChange('errorCaptureEnabled')}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <ConfigCard
                    name="errorCapture.projectId"
                    type="string"
                    required
                    description={t(
                      "Your project ID for error tracking association."
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <ConfigCard
                    name="errorCapture.autoCreateBugs"
                    type="boolean"
                    description={t(
                      "Automatically create bug reports for captured errors."
                    )}
                    defaultValue="false"
                    toggleEnabled
                    isToggled={configToggles.autoCreateBugs}
                    onToggle={() => handleToggleChange('autoCreateBugs')}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <ConfigCard
                    name="errorCapture.maxErrors"
                    type="number"
                    description={t(
                      "Maximum number of errors to capture per session."
                    )}
                    defaultValue="100"
                    toggleEnabled
                    isToggled={configToggles.maxErrors}
                    onToggle={() => handleToggleChange('maxErrors')}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <ConfigCard
                    name="errorCapture.captureConsole"
                    type="boolean"
                    description={t(
                      "Capture console.error and console.warn messages."
                    )}
                    defaultValue="true"
                    toggleEnabled
                    isToggled={configToggles.captureConsole}
                    onToggle={() => handleToggleChange('captureConsole')}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <ConfigCard
                    name="errorCapture.captureNetwork"
                    type="boolean"
                    description={t(
                      "Capture failed network requests (4xx, 5xx errors)."
                    )}
                    defaultValue="true"
                    toggleEnabled
                    isToggled={configToggles.captureNetwork}
                    onToggle={() => handleToggleChange('captureNetwork')}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <ConfigCard
                    name="errorCapture.captureGlobalErrors"
                    type="boolean"
                    description={t(
                      "Capture unhandled JavaScript errors and promise rejections."
                    )}
                    defaultValue="true"
                    toggleEnabled
                    isToggled={configToggles.captureGlobalErrors}
                    onToggle={() => handleToggleChange('captureGlobalErrors')}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Event Callbacks Accordion */}
          <Accordion
            defaultExpanded
            elevation={0}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "12px !important",
              mb: 2,
              "&:before": { display: "none" },
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{
                backgroundColor: alpha("#8b5cf6", 0.04),
                "&:hover": {
                  backgroundColor: alpha("#8b5cf6", 0.08),
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    backgroundColor: alpha("#8b5cf6", 0.1),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <WebhookOutlined sx={{ color: "#8b5cf6", fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {t("Event Callbacks")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("Hook into widget lifecycle events")}
                  </Typography>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 2 }}>
              <Box sx={{ mb: 2, p: 1.5, backgroundColor: alpha("#8b5cf6", 0.08), borderRadius: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {t("Toggle switches to add callback functions to your snippet.")}
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <CallbackCard
                    name="onBugCreated"
                    params="bugLink: string"
                    description={t(
                      "Called when a bug report is automatically created from a captured error. Receives the URL to the created bug."
                    )}
                    toggleEnabled
                    isToggled={configToggles.onBugCreated}
                    onToggle={() => handleToggleChange('onBugCreated')}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <CallbackCard
                    name="onError"
                    params="error: Error"
                    description={t(
                      "Called when an error occurs within the widget itself. Useful for debugging integration issues."
                    )}
                    toggleEnabled
                    isToggled={configToggles.onError}
                    onToggle={() => handleToggleChange('onError')}
                  />
                </Grid>
              </Grid>

              {/* Event Subscriptions */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                  {t("Event Subscriptions")}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  {t("Subscribe to these events using widget.on(event, callback). Returns an unsubscribe function.")}
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <CallbackCard
                      name="bug:created"
                      params="{ bugLink }"
                      description={t(
                        "Fired in any mode when a bug is successfully created."
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <CallbackCard
                      name="trigger:operationComplete"
                      params="{ success, bugLink?, bugId?, cancelled? }"
                      description={t(
                        "Fired when an operation finishes in trigger mode. Indicates success or cancellation."
                      )}
                      modeTag="Trigger"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <CallbackCard
                      name="auth:stateChanged"
                      params="{ isAuthenticated }"
                      description={t(
                        "Fired when authentication state changes in authenticated mode."
                      )}
                      modeTag="Auth"
                    />
                  </Grid>
                </Grid>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Widget Methods Accordion */}
          <Accordion
            defaultExpanded
            elevation={0}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "12px !important",
              mb: 2,
              "&:before": { display: "none" },
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{
                backgroundColor: alpha(theme.palette.info.main, 0.04),
                "&:hover": {
                  backgroundColor: alpha(theme.palette.info.main, 0.08),
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.info.main, 0.1),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <BuildOutlined sx={{ color: theme.palette.info.main, fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {t("Widget Methods")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("Available methods on the widget instance")}
                  </Typography>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <MethodCard
                    name="destroy()"
                    returns="void"
                    description={t(
                      "Remove the widget and clean up all resources."
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <MethodCard
                    name="show()"
                    returns="void"
                    description={t(
                      "Show the floating button. Primarily used in trigger mode to display the widget."
                    )}
                    modeTag="Trigger"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <MethodCard
                    name="hide()"
                    returns="void"
                    description={t(
                      "Hide the floating button without waiting for an operation to complete."
                    )}
                    modeTag="Trigger"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <MethodCard
                    name="on(event, callback)"
                    returns="() => void"
                    description={t(
                      "Subscribe to widget events. Returns an unsubscribe function."
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <MethodCard
                    name="isAuthenticated()"
                    returns="boolean"
                    description={t(
                      "Check if the user is currently logged in."
                    )}
                    modeTag="Auth"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <MethodCard
                    name="logout()"
                    returns="void"
                    description={t(
                      "Clear authentication data and log the user out."
                    )}
                    modeTag="Auth"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <MethodCard
                    name="getCapabilities()"
                    returns="object"
                    description={t(
                      "Get browser feature support information."
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <MethodCard
                    name="getErrors()"
                    returns="Error[]"
                    description={t(
                      "Get all captured errors from the current session."
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <MethodCard
                    name="clearErrors()"
                    returns="void"
                    description={t(
                      "Clear all captured errors from the current session."
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <MethodCard
                    name="getErrorSummary()"
                    returns="object"
                    description={t(
                      "Get a summary of captured errors including counts by type."
                    )}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Divider sx={{ my: 4 }} />

          {/* Troubleshooting */}
          <Typography variant="h6" gutterBottom fontWeight={500}>
            {t("Troubleshooting")}
          </Typography>
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <StyledAlert type="error" title={t("Script not loading")}>
              {t(
                "Ensure the script tag is placed in the <head> section and loads before the initialization code. Check browser console for network errors."
              )}
            </StyledAlert>
            <StyledAlert type="error" title={t("Errors not being captured")}>
              {t(
                "Make sure errorCapture.enabled is set to true and projectId is correctly configured. Verify the decryption key is valid."
              )}
            </StyledAlert>
            <StyledAlert type="error" title={t("Bugs not being created")}>
              {t(
                "Ensure autoCreateBugs is set to true. Check that maxErrors limit hasn't been reached for the session."
              )}
            </StyledAlert>
            {selectedMode === 'public' && (
              <StyledAlert type="error" title={t("Public mode — Missing projectId or companyId")}>
                {t(
                  "Both projectId and companyId are required for public mode. The widget will throw an error at initialization if either is missing. Check the browser console for '[SnifferWidget] projectId is required' or '[SnifferWidget] companyId is required' messages."
                )}
              </StyledAlert>
            )}
            {selectedMode === 'trigger' && (
              <>
                <StyledAlert type="error" title={t("Trigger mode — Widget not appearing")}>
                  {t(
                    "In trigger mode, the floating button is hidden by default. You must call widget.show() to display it, or use triggerQueryParam to auto-show via a URL parameter. Make sure your initialization assigns the result to a variable: var widget = SnifferWidget.init({...})."
                  )}
                </StyledAlert>
                <StyledAlert type="error" title={t("Trigger mode — triggerQueryParam not working")}>
                  {t(
                    "Ensure the query parameter name matches your configuration. If triggerQueryParam is true, use ?sniffer in the URL. If set to a custom string (e.g., 'report-bug'), use that exact parameter name. The parameter is automatically removed from the URL after activation."
                  )}
                </StyledAlert>
                <StyledAlert type="error" title={t("Trigger mode — Missing projectId or companyId")}>
                  {t(
                    "Trigger mode uses public endpoints and requires both projectId and companyId. The widget will throw an error at initialization if either is missing."
                  )}
                </StyledAlert>
              </>
            )}
          </Box>
    </Box>
  );

  if (mode === "page") {
    return (
      <Box
        sx={{
          backgroundColor: theme.palette.background.default,
          height: "calc(100vh - 120px)",
          overflowY: "auto",
          py: 4,
          px: 3,
        }}
      >
        <Box sx={{ maxWidth: 1000, mx: "auto", mb: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
            <Typography variant="h4" fontWeight={600}>
              {t("Live Logs Docs")}
            </Typography>
            <Chip
              label={t("Documentation")}
              color="primary"
              size="small"
              variant="outlined"
            />
          </Box>
          <Typography variant="body1" color="text.secondary">
            {t("Integrate Live Logs Docs for automatic error tracking and bug creation")}
          </Typography>
        </Box>
        {DocumentationContent}
      </Box>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: `1px solid ${theme.palette.divider}`,
          py: 2,
          px: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="h5" fontWeight={600}>
            {t("Sniffer CDN Integration")}
          </Typography>
          <Chip
            label={t("Documentation")}
            color="primary"
            size="small"
            variant="outlined"
          />
        </Box>
        <IconButton onClick={onClose} size="large">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 4 }}>
        {DocumentationContent}
      </DialogContent>
    </Dialog>
  );
};

export default SnifferCDNDocumentation;
